import { RestClientApi } from '../restClient/restClientApi';

export type RancherProject = {
  name: string;
  namespace: string;
  cluster: string;
  enviroment: string;
  limitsMemory: string;
  limitsCpu: string;
};

export type RancherEnvironment = {
  name: string;
  base_url: string;
};

export type RancherCluster = {
  name: string;
  cluster_id: string;
  environment: RancherEnvironment;
  state: string;
  provider: string;
  pci: boolean;
  runway_status: string;
  dashboard_url: string;
  url: string;
  allowed_aad_groups: string[];
};

export interface ClusterServiceApi {
  getAllClusters(): Promise<RancherCluster[]>;
  getClustersByEnvironment(clusterName: string): Promise<{ name: string; cluster_id: string }[]>;
  getProjectsByCluster(clusterName: string): Promise<RancherProject[]>;
  getOrphanedApplicationsByCluster(
    clusterName: string,
    cluster_id: string,
  ): Promise<
    {
      cluster_id: string;
      name: string;
      destination_namespace: string;
      project: string;
    }[]
  >;

  getAzureAdGroupsByProjectName(clusterName: string, projectName: string): Promise<string[]>;
  deleteApplicationByAppName(clusterName: string, clusterId: string, appName: string): Promise<null>;
  deleteProjectByAppName(clusterName: string, clusterId: string, appName: string): Promise<null>;
}

export default class ClusterService implements ClusterServiceApi {
  constructor(
    private clusterServiceUrl: string,
    private token: string,
    private restClient: RestClientApi,
  ) {}

  private rancherEnvironment = (clusterName: string) => {
    if (clusterName.includes('-np-')) return 'nonprod';
    else if (clusterName.includes('-lab-')) return 'lab';
    else if (clusterName.includes('-p-')) return 'prod';
    throw new Error(`Unknown cluster environment for cluster: ${clusterName}`);
  };

  getToken = async () => this.token;

  getAllClusters = async () => {
    const sso = await this.getToken();
    const result = await this.restClient.get<RancherCluster[]>(`${this.clusterServiceUrl}/v2/clusters`, {
      headers: {
        authorization: `Bearer ${sso}`,
      },
    });
    return result.data;
  };

  getClustersByEnvironment = async (clusterName: string) => {
    const sso = await this.getToken();
    const rancherEnv = this.rancherEnvironment(clusterName);
    const result = await this.restClient.get<{ name: string; cluster_id: string }[]>(
      `${this.clusterServiceUrl}/v2/${rancherEnv}/clusters/`,
      {
        headers: {
          authorization: `Bearer ${sso}`,
        },
      },
    );
    return result.data;
  };

  getProjectsByCluster = async (clusterName: string) => {
    const sso = await this.getToken();
    const result = await this.restClient.get<RancherProject[]>(
      `${this.clusterServiceUrl}/projects?clusterNames=${clusterName}`,
      {
        headers: {
          authorization: `Bearer ${sso}`,
        },
      },
    );
    return result.data;
  };

  getOrphanedApplicationsByCluster = async (clusterName: string, clusterId: string) => {
    const sso = await this.getToken();
    const rancherEnv = this.rancherEnvironment(clusterName);
    const result = await this.restClient.get<
      {
        cluster_id: string;
        name: string;
        destination_namespace: string;
        project: string;
      }[]
    >(`${this.clusterServiceUrl}/v2/${rancherEnv}/clusters/${clusterId}/deployments/applications?sync_status=Unknown`, {
      headers: {
        authorization: `Bearer ${sso}`,
      },
    });
    return result.data;
  };

  getAzureAdGroupsByProjectName = async (clusterName: string, projectName: string) => {
    const sso = await this.getToken();
    const { data: result } = await this.restClient.get<{ status: number; data: string[] }>(
      `${this.clusterServiceUrl}/projects/aad?cluster_name=${clusterName}&project_name=${projectName}`,
      {
        headers: {
          authorization: `Bearer ${sso}`,
        },
      },
    );
    return result.data;
  };

  deleteApplicationByAppName = async (clusterName: string, clusterId: string, appName: string) => {
    const sso = await this.getToken();
    const rancherEnv = this.rancherEnvironment(clusterName);
    const result = await this.restClient.delete<null>(
      `${this.clusterServiceUrl}/v2/${rancherEnv}/clusters/${clusterId}/deployments/applications/${appName}`,
      {
        headers: {
          authorization: `Bearer ${sso}`,
        },
      },
    );
    return result.data;
  };

  deleteProjectByAppName = async (clusterName: string, clusterId: string, appName: string) => {
    const sso = await this.getToken();
    const rancherEnv = this.rancherEnvironment(clusterName);
    const result = await this.restClient.delete<null>(
      `${this.clusterServiceUrl}/v2/${rancherEnv}/clusters/${clusterId}/deployments/projects/${appName}`,
      {
        headers: {
          authorization: `Bearer ${sso}`,
        },
      },
    );
    return result.data;
  };
}
